import {
    Alert, Badge, Box, Button, Chip, CircularProgress,
    InputAdornment, Snackbar, Tab, TablePagination, Tabs, TextField, Typography
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { useCallback, useEffect, useRef, useState } from 'react';
import moment from 'moment';
import ApiServices from '../../services/ApiServices';

// ── Polling ─────────────────────────────────────────────────────────────────
const POLL_INTERVAL_MS = 5000;

// ── Device OTA stage helpers ─────────────────────────────────────────────────
const STAGE_LABELS = {
    PENDING:                          'Pending',
    REQUEST_ACCEPTED:                 'Request Accepted',
    REQUEST_REJECTED_ACTIVE_SESSION:  'Rejected — Active Session',
    DOWNLOAD_STARTED:                 'Download Started',
    DOWNLOAD_SUCCESS:                 'Download Success',
    DOWNLOAD_FAILED:                  'Download Failed',
    OTA_INITIATED:                    'OTA Initiated',
    TRANSFER_IN_PROGRESS:             'Transfer In Progress',
    TRANSFER_COMPLETE:                'Transfer Complete',
    INSTALLATION_IN_PROGRESS:         'Installation In Progress',
    OTA_SUCCESS:                      'OTA Success',
    OTA_FAILED:                       'OTA Failed',
    STOP_REQUEST_ACCEPTED:            'Stop Accepted',
    STOPPED:                          'Stopped',
    STOP_REJECTED_NO_ACTIVE_SESSION:  'Stop Rejected — No Active Session',
};

const stageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_SUCCESS') return 'success';
    if (stage === 'OTA_FAILED' || stage === 'DOWNLOAD_FAILED') return 'error';
    if (stage === 'STOPPED' || stage === 'STOP_REJECTED_NO_ACTIVE_SESSION') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

// ── Gateway OTA stage helpers ─────────────────────────────────────────────────
const GW_STAGE_LABELS = {
    PENDING:            'Pending',
    REQUEST_ACCEPTED:   'Request Accepted',
    REQUEST_REJECTED:   'Rejected',
    IN_PROGRESS:        'In Progress',
    OTA_COMPLETE:       'OTA Complete',
    OTA_FAILED:         'OTA Failed',
    ABORT_ACCEPTED:     'Abort Accepted',
    ABORT_REJECTED:     'Abort Rejected',
};

const gwStageColor = (stage) => {
    if (!stage) return 'default';
    if (stage === 'OTA_COMPLETE') return 'success';
    if (stage === 'OTA_FAILED' || stage === 'REQUEST_REJECTED' || stage === 'ABORT_REJECTED') return 'error';
    if (stage === 'ABORT_ACCEPTED') return 'warning';
    if (stage === 'PENDING') return 'default';
    return 'info';
};

// ── Cabinet list columns ─────────────────────────────────────────────────────
const CABINET_COLUMNS = [
    { field: 'gatewayId', headerName: 'Gateway ID', flex: 1, sortable: false },
    {
        field: 'softwareVersion', headerName: 'Gateway', flex: 0.7, sortable: false,
        renderCell: (params) => params.row.softwareVersion ?? 'N/A'
    },
    {
        field: 'coordinatorVersion', headerName: 'Coordinator', flex: 0.7, sortable: false,
        renderCell: (params) => params.row.coordinatorVersion ?? 'N/A'
    },
    {
        field: 'cabinetVersion', headerName: 'Cabinet Controller', flex: 0.7, sortable: false,
        renderCell: (params) => params.row.cabinetVersion?.trim() || 'N/A'
    },
    {
        field: 'jennicChipType', headerName: 'Chip Type', flex: 0.7, sortable: false,
        renderCell: (params) => params.row.jennicChipType ?? 'N/A'
    },
    {
        field: 'lastSeen', headerName: 'Last Seen', flex: 1, sortable: false,
        renderCell: (params) => params.row.lastSeen
            ? moment(params.row.lastSeen).format('DD-MM-YYYY HH:mm:ss')
            : 'N/A'
    },
];

// SkyHubs have no cabinet controller — same columns minus that one
const SKYHUB_COLUMNS = CABINET_COLUMNS.filter(col => col.field !== 'cabinetVersion');

// ── Active device OTA columns ────────────────────────────────────────────────
const activeOtaColumns = (onSelect) => [
    {
        field: 'gatewayId', headerName: 'Gateway ID', flex: 1, sortable: false,
        renderCell: (params) => (
            <Typography
                variant="body2"
                sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={(e) => { e.stopPropagation(); onSelect(params.value); }}
            >
                {params.value}
            </Typography>
        )
    },
    { field: 'otaId', headerName: 'OTA ID', flex: 1, sortable: false },
    {
        field: 'stage', headerName: 'Stage', flex: 1.1, sortable: false,
        renderCell: (params) => (
            <Chip
                label={STAGE_LABELS[params.value] ?? params.value ?? '—'}
                color={stageColor(params.value)}
                size="small"
            />
        )
    },
    {
        field: 'progress', headerName: 'Progress', flex: 0.7, sortable: false,
        renderCell: (params) => {
            const { completedDevices, totalDevices } = params.row;
            if (totalDevices == null) return '—';
            return `${completedDevices ?? 0} / ${totalDevices}`;
        }
    },
    {
        field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
        renderCell: (params) => params.value
            ? moment(params.value).format('DD-MM-YYYY HH:mm:ss')
            : '—'
    },
];

// ── Active gateway OTA columns ───────────────────────────────────────────────
const activeGwOtaColumns = (onSelect) => [
    {
        field: 'gatewayId', headerName: 'Gateway ID', flex: 1, sortable: false,
        renderCell: (params) => (
            <Typography
                variant="body2"
                sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={(e) => { e.stopPropagation(); onSelect(params.value); }}
            >
                {params.value}
            </Typography>
        )
    },
    { field: 'otaId', headerName: 'OTA ID', flex: 1.2, sortable: false },
    {
        field: 'stage', headerName: 'Stage', flex: 1, sortable: false,
        renderCell: (params) => (
            <Chip
                label={GW_STAGE_LABELS[params.value] ?? params.value ?? '—'}
                color={gwStageColor(params.value)}
                size="small"
            />
        )
    },
    {
        field: 'forceUpdate', headerName: 'Force Update', flex: 0.7, sortable: false,
        renderCell: (params) => params.value ? 'Yes' : 'No'
    },
    {
        field: 'updatedAt', headerName: 'Updated', flex: 1, sortable: false,
        renderCell: (params) => params.value
            ? moment(params.value).format('DD-MM-YYYY HH:mm:ss')
            : '—'
    },
];

// ── Component ────────────────────────────────────────────────────────────────
function GatewayTable({ onSelect }) {
    const [tab, setTab] = useState(0);
    const [activeOtaSubTab, setActiveOtaSubTab] = useState(0);

    // Cabinets tab state
    const [cabinets, setCabinets] = useState([]);
    const [cabinetsLoading, setCabinetsLoading] = useState(false);
    const [cabinetsPage, setCabinetsPage] = useState(0);
    const [cabinetsRowsPerPage, setCabinetsRowsPerPage] = useState(10);
    const [cabinetsTotalRows, setCabinetsTotalRows] = useState(0);
    const [cabinetsSearchInput, setCabinetsSearchInput] = useState('');
    const [cabinetsSearchQuery, setCabinetsSearchQuery] = useState('');

    // SkyHubs tab state
    const [skyhubs, setSkyhubs] = useState([]);
    const [skyhubsLoading, setSkyhubsLoading] = useState(false);
    const [skyhubsPage, setSkyhubsPage] = useState(0);
    const [skyhubsRowsPerPage, setSkyhubsRowsPerPage] = useState(10);
    const [skyhubsTotalRows, setSkyhubsTotalRows] = useState(0);
    const [skyhubsSearchInput, setSkyhubsSearchInput] = useState('');
    const [skyhubsSearchQuery, setSkyhubsSearchQuery] = useState('');

    // Active device OTA state
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(true);

    // Active gateway OTA state
    const [gwSessions, setGwSessions] = useState([]);
    const [gwSessionsLoading, setGwSessionsLoading] = useState(true);

    const timerRef = useRef(null);

    const [alertObj, setAlertObj] = useState({ open: false, severity: 'error', message: '' });

    // ── Cabinets fetch ───────────────────────────────────────────────────────
    const fetchCabinets = useCallback(() => {
        setCabinetsLoading(true);
        ApiServices.getGateways(cabinetsPage, cabinetsRowsPerPage, cabinetsSearchQuery, true)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setCabinets([]);
                    setCabinetsTotalRows(0);
                } else {
                    setCabinets(response.data.content ?? []);
                    setCabinetsTotalRows(response.data.totalElements ?? 0);
                }
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setCabinetsLoading(false));
    }, [cabinetsPage, cabinetsRowsPerPage, cabinetsSearchQuery]);

    useEffect(() => { fetchCabinets(); }, [fetchCabinets]);

    // ── SkyHubs fetch ────────────────────────────────────────────────────────
    const fetchSkyhubs = useCallback(() => {
        setSkyhubsLoading(true);
        ApiServices.getGateways(skyhubsPage, skyhubsRowsPerPage, skyhubsSearchQuery, false)
            .then(response => {
                if (response.status === 204 || !response.data) {
                    setSkyhubs([]);
                    setSkyhubsTotalRows(0);
                } else {
                    setSkyhubs(response.data.content ?? []);
                    setSkyhubsTotalRows(response.data.totalElements ?? 0);
                }
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setSkyhubsLoading(false));
    }, [skyhubsPage, skyhubsRowsPerPage, skyhubsSearchQuery]);

    useEffect(() => { fetchSkyhubs(); }, [fetchSkyhubs]);

    // ── Active sessions fetch + polling ──────────────────────────────────────
    const fetchSessions = useCallback(() => {
        ApiServices.getActiveOtaSessions()
            .then(response => {
                setSessions(response.status === 204 || !response.data ? [] : response.data);
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setSessionsLoading(false));
    }, []);

    const fetchGwSessions = useCallback(() => {
        ApiServices.getActiveGwSelfOtaSessions()
            .then(response => {
                setGwSessions(response.status === 204 || !response.data ? [] : response.data);
            })
            .catch(error => {
                setAlertObj({ open: true, severity: 'error', message: error.response?.data ?? error.message });
            })
            .finally(() => setGwSessionsLoading(false));
    }, []);

    useEffect(() => {
        fetchSessions();
        fetchGwSessions();
        timerRef.current = setInterval(() => {
            fetchSessions();
            fetchGwSessions();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(timerRef.current);
    }, [fetchSessions, fetchGwSessions]);

    // ── Cabinets search handlers ──────────────────────────────────────────────
    const handleCabinetsSearch = () => {
        setCabinetsSearchQuery(cabinetsSearchInput.trim().toLowerCase());
        setCabinetsPage(0);
    };

    const handleCabinetsSearchKeyDown = (e) => { if (e.key === 'Enter') handleCabinetsSearch(); };

    const handleCabinetsClear = () => {
        setCabinetsSearchInput('');
        setCabinetsSearchQuery('');
        setCabinetsPage(0);
    };

    // ── SkyHubs search handlers ───────────────────────────────────────────────
    const handleSkyhubsSearch = () => {
        setSkyhubsSearchQuery(skyhubsSearchInput.trim().toLowerCase());
        setSkyhubsPage(0);
    };

    const handleSkyhubsSearchKeyDown = (e) => { if (e.key === 'Enter') handleSkyhubsSearch(); };

    const handleSkyhubsClear = () => {
        setSkyhubsSearchInput('');
        setSkyhubsSearchQuery('');
        setSkyhubsPage(0);
    };

    const totalActiveSessions = sessions.length + gwSessions.length;

    // ── Render ───────────────────────────────────────────────────────────────
    return (
        <Box>
            <Snackbar
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                autoHideDuration={3000}
                open={alertObj.open}
                onClose={() => setAlertObj(a => ({ ...a, open: false }))}
            >
                <Alert severity={alertObj.severity} sx={{ width: '100%' }}>{alertObj.message}</Alert>
            </Snackbar>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="Cabinets" />
                <Tab label="SkyHubs" />
                <Tab
                    label={
                        <Badge badgeContent={totalActiveSessions} color="warning" max={99}>
                            <Box sx={{ pr: totalActiveSessions > 0 ? 1.5 : 0 }}>Active OTA</Box>
                        </Badge>
                    }
                />
            </Tabs>

            {/* ── Tab 0: Cabinets ── */}
            {tab === 0 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Search by Gateway ID"
                                value={cabinetsSearchInput}
                                onChange={e => setCabinetsSearchInput(e.target.value)}
                                onKeyDown={handleCabinetsSearchKeyDown}
                                sx={{ width: 260 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button variant="contained" size="small" onClick={handleCabinetsSearch}>Search</Button>
                            {cabinetsSearchQuery && (
                                <Button variant="outlined" size="small" onClick={handleCabinetsClear}>Clear</Button>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TablePagination
                                component="div"
                                count={cabinetsTotalRows}
                                page={cabinetsPage}
                                onPageChange={(_, newPage) => setCabinetsPage(newPage)}
                                rowsPerPage={cabinetsRowsPerPage}
                                onRowsPerPageChange={e => { setCabinetsRowsPerPage(parseInt(e.target.value, 10)); setCabinetsPage(0); }}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                            />
                        </Box>
                    </Box>

                    {cabinetsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={cabinets}
                            columns={CABINET_COLUMNS}
                            getRowId={(row) => row.gatewayId}
                            autoHeight
                            hideFooter
                            disableColumnMenu
                            disableSelectionOnClick
                            onRowClick={(params) => onSelect(params.row.gatewayId)}
                            sx={{ cursor: 'pointer' }}
                            slots={{
                                noRowsOverlay: () => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="text.secondary">
                                            {cabinetsSearchQuery ? `No cabinets matching "${cabinetsSearchQuery}"` : 'No cabinets found'}
                                        </Typography>
                                    </Box>
                                )
                            }}
                        />
                    )}
                </>
            )}

            {/* ── Tab 1: SkyHubs ── */}
            {tab === 1 && (
                <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <TextField
                                size="small"
                                placeholder="Search by Gateway ID"
                                value={skyhubsSearchInput}
                                onChange={e => setSkyhubsSearchInput(e.target.value)}
                                onKeyDown={handleSkyhubsSearchKeyDown}
                                sx={{ width: 260 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search fontSize="small" />
                                        </InputAdornment>
                                    )
                                }}
                            />
                            <Button variant="contained" size="small" onClick={handleSkyhubsSearch}>Search</Button>
                            {skyhubsSearchQuery && (
                                <Button variant="outlined" size="small" onClick={handleSkyhubsClear}>Clear</Button>
                            )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TablePagination
                                component="div"
                                count={skyhubsTotalRows}
                                page={skyhubsPage}
                                onPageChange={(_, newPage) => setSkyhubsPage(newPage)}
                                rowsPerPage={skyhubsRowsPerPage}
                                onRowsPerPageChange={e => { setSkyhubsRowsPerPage(parseInt(e.target.value, 10)); setSkyhubsPage(0); }}
                                rowsPerPageOptions={[10, 20, 50, 100]}
                            />
                        </Box>
                    </Box>

                    {skyhubsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <DataGrid
                            rows={skyhubs}
                            columns={SKYHUB_COLUMNS}
                            getRowId={(row) => row.gatewayId}
                            autoHeight
                            hideFooter
                            disableColumnMenu
                            disableSelectionOnClick
                            onRowClick={(params) => onSelect(params.row.gatewayId)}
                            sx={{ cursor: 'pointer' }}
                            slots={{
                                noRowsOverlay: () => (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                        <Typography color="text.secondary">
                                            {skyhubsSearchQuery ? `No SkyHubs matching "${skyhubsSearchQuery}"` : 'No SkyHubs found'}
                                        </Typography>
                                    </Box>
                                )
                            }}
                        />
                    )}
                </>
            )}

            {/* ── Tab 2: Active OTA Sessions ── */}
            {tab === 2 && (
                <>
                    <Tabs
                        value={activeOtaSubTab}
                        onChange={(_, v) => setActiveOtaSubTab(v)}
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab
                            label={
                                <Badge badgeContent={sessions.length} color="info" max={99}>
                                    <Box sx={{ pr: sessions.length > 0 ? 1.5 : 0 }}>Device OTA</Box>
                                </Badge>
                            }
                        />
                        <Tab
                            label={
                                <Badge badgeContent={gwSessions.length} color="info" max={99}>
                                    <Box sx={{ pr: gwSessions.length > 0 ? 1.5 : 0 }}>Gateway OTA</Box>
                                </Badge>
                            }
                        />
                    </Tabs>

                    {/* Sub-tab 0: Device OTA */}
                    {activeOtaSubTab === 0 && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1, minHeight: 36 }}>
                                {sessionsLoading && <CircularProgress size={18} />}
                            </Box>
                            <DataGrid
                                rows={sessions}
                                columns={activeOtaColumns(onSelect)}
                                getRowId={(row) => row.id}
                                autoHeight
                                hideFooter
                                disableColumnMenu
                                disableSelectionOnClick
                                slots={{
                                    noRowsOverlay: () => (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <Typography color="text.secondary">No active device OTA sessions</Typography>
                                        </Box>
                                    )
                                }}
                            />
                        </>
                    )}

                    {/* Sub-tab 1: Gateway OTA */}
                    {activeOtaSubTab === 1 && (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 1, minHeight: 36 }}>
                                {gwSessionsLoading && <CircularProgress size={18} />}
                            </Box>
                            <DataGrid
                                rows={gwSessions}
                                columns={activeGwOtaColumns(onSelect)}
                                getRowId={(row) => row.id}
                                autoHeight
                                hideFooter
                                disableColumnMenu
                                disableSelectionOnClick
                                slots={{
                                    noRowsOverlay: () => (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                                            <Typography color="text.secondary">No active gateway OTA sessions</Typography>
                                        </Box>
                                    )
                                }}
                            />
                        </>
                    )}
                </>
            )}

        </Box>
    );
}

export default GatewayTable;
